import { useCallback, useMemo } from 'react';
import type { FocusEventHandler, SyntheticEvent } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { matchSorter } from 'match-sorter';

import type { ThirdPartyForm_LegalStatus } from '../../hooks/useLegalStatusesQuery.hook';

interface OptionRow {
  readonly displayLabel: string;
  readonly isCanonical: boolean;
  readonly key: string;
  readonly value: string;
}

const buildOptionRows = (
  legalStatuses: readonly ThirdPartyForm_LegalStatus[],
): readonly OptionRow[] =>
  legalStatuses.flatMap((legalStatus) => [
    {
      displayLabel: legalStatus.label,
      isCanonical: true,
      key: legalStatus._id,
      value: legalStatus._id,
    },
    ...legalStatus.aliases.map((alias, index) => ({
      displayLabel: `${alias} (= ${legalStatus.label})`,
      isCanonical: false,
      key: `${legalStatus._id}#alias#${index.toFixed()}`,
      value: legalStatus._id,
    })),
  ]);

interface FilterState {
  readonly inputValue: string;
}

interface Props {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly label: string;
  readonly loading?: boolean;
  readonly name?: string;
  readonly noOptionsText?: string;
  readonly onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  readonly onChange: (legalStatusId: string | null) => void;
  readonly options: readonly ThirdPartyForm_LegalStatus[];
  readonly required?: boolean;
  readonly value?: string | null;
}

export const LegalStatusIdAutocomplete = ({
  disabled = false,
  error = false,
  helperText,
  label,
  loading = false,
  name,
  noOptionsText = 'Aucun statut juridique trouvé',
  onBlur,
  onChange,
  options,
  required = false,
  value,
}: Readonly<Props>) => {
  const rows = useMemo(() => buildOptionRows(options), [options]);

  const selectedRow = useMemo(
    () =>
      value
        ? (rows.find((row) => row.isCanonical && row.value === value) ?? null)
        : null,
    [rows, value],
  );

  const filterOptions = useCallback(
    (
      rowsToFilter: readonly OptionRow[],
      { inputValue }: Readonly<FilterState>,
    ) => matchSorter(rowsToFilter, inputValue, { keys: ['displayLabel'] }),
    [],
  );

  const handleChange = useCallback(
    (_event: Readonly<SyntheticEvent>, row: OptionRow | null) => {
      onChange(row?.value ?? null);
    },
    [onChange],
  );

  const getOptionKey = useCallback((row: Readonly<OptionRow>) => row.key, []);

  const getOptionLabel = useCallback(
    (row: Readonly<OptionRow>) => row.displayLabel,
    [],
  );

  const isOptionEqualToValue = useCallback(
    (row: Readonly<OptionRow>, selectedValue: Readonly<OptionRow>) =>
      row.value === selectedValue.value,
    [],
  );

  const renderInput = useCallback(
    (params: Readonly<AutocompleteRenderInputParams>) => (
      <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        disabled={disabled}
        error={error}
        helperText={helperText}
        label={label}
        name={name}
        onBlur={onBlur}
        required={required}
      />
    ),
    [disabled, error, helperText, label, name, onBlur, required],
  );

  return (
    <Autocomplete<OptionRow>
      disabled={disabled || loading}
      filterOptions={filterOptions}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      loadingText="Chargement ..."
      noOptionsText={noOptionsText}
      onChange={handleChange}
      options={rows}
      renderInput={renderInput}
      value={selectedRow}
    />
  );
};
